<template>
    <themed-modal
        name="document-upload"
        :title="label"
        size="lg"
        :click-to-close="false"
        @opened="onModalOpened"
        @closed="onModalClosed"
    >
        <div class="upload-document-native">
            <!-- Info Message -->
            <div class="upload-document-native__info">
                Select up to <strong>{{ maxFiles }}</strong> files to upload. Max file size is <strong>150MB.</strong>
            </div>

            <!-- Error Messages -->
            <div v-if="errors.length" class="upload-document-native__errors">
                <div v-for="(error, index) in errors" :key="index" class="upload-document-native__error">
                    <span class="upload-document-native__error-file">{{ error.fileName }}</span> - 
                    <span class="upload-document-native__error-text">{{ error.text }}</span>
                </div>
            </div>

            <!-- Upload Area -->
            <div
                class="upload-document-native__dropzone"
                :class="{ 'upload-document-native__dropzone--dragging': isDragging }"
                @click="openFileDialog"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
            >
                <input
                    ref="fileInput"
                    type="file"
                    multiple
                    :accept="acceptedFiles"
                    @change="onFileSelect"
                    style="display: none;"
                />
                
                <div v-if="files.length === 0" class="upload-document-native__dropzone-message">
                    <theme-icon icon="upload" type="icons" class="upload-document-native__upload-icon" />
                    <span class="upload-document-native__upload-text">Click here or drag files to {{ label.toLowerCase() }}</span>
                </div>
            </div>

            <!-- File Previews -->
            <div v-if="files.length > 0" class="upload-document-native__files">
                <div
                    v-for="(file, index) in files"
                    :key="file.id"
                    class="upload-document-native__file"
                >
                    <!-- File Row: Image + Fields in one line -->
                    <div class="upload-document-native__file-row">
                        <!-- File Preview Image with filename below -->
                        <div class="upload-document-native__file-preview-wrapper">
                            <div class="upload-document-native__file-image">
                                <img v-if="file.preview" :src="file.preview" :alt="file.name" />
                                <theme-icon v-else icon="file" type="icons" class="upload-document-native__file-icon" />
                                
                                <!-- Success/Error Indicators -->
                                <div v-if="file.uploaded" class="upload-document-native__file-success">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54 54">
                                        <path d="m23.5 31.843-5.915-5.915a4 4 0 0 0-5.657 5.657l8.487 8.487c.098.098.2.19.307.277a4.005 4.005 0 0 0 5.624-.038l16.965-16.965a4 4 0 1 0-5.657-5.657L23.5 31.843ZM27 53c14.36 0 26-11.64 26-26S41.36 1 27 1 1 12.64 1 27s11.64 26 26 26Z" stroke-opacity=".199" stroke="#747474" fill-opacity=".817" fill="#FFF" fill-rule="evenodd"/>
                                    </svg>
                                </div>
                                <div v-if="file.error" class="upload-document-native__file-error-mark">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54 54">
                                        <path d="m32.657 29 5.654-5.654a4 4 0 1 0-5.657-5.657L27 23.343l-5.654-5.654a4 4 0 1 0-5.657 5.657L21.343 29l-5.654 5.654a4 4 0 1 0 5.657 5.657L27 34.657l5.654 5.654a4 4 0 1 0 5.657-5.657L32.657 29ZM27 53c14.36 0 26-11.64 26-26S41.36 1 27 1 1 12.64 1 27s11.64 26 26 26Z" stroke="#747474" stroke-opacity=".199" fill="#FFF" fill-opacity=".817" fill-rule="evenodd"/>
                                    </svg>
                                </div>
                            </div>
                            <!-- File Name & Size below image -->
                            <div class="upload-document-native__file-info">
                                <div class="upload-document-native__file-name" :title="file.name">{{ file.name }}</div>
                                <div class="upload-document-native__file-size">{{ formatFileSize(file.size) }}</div>
                            </div>
                        </div>

                        <!-- Form Fields -->
                        <div class="upload-document-native__file-fields">
                            <div class="upload-document-native__field">
                                <label class="upload-document-native__label">Description</label>
                                <themed-input
                                    v-model="file.description"
                                    type="text"
                                    :disabled="file.uploading || file.uploaded"
                                />
                            </div>
                            <div class="upload-document-native__field">
                                <label class="upload-document-native__label">Category</label>
                                <themed-select
                                    v-model="file.categoryId"
                                    :options="categoryOptions"
                                    :disabled="file.uploading || file.uploaded"
                                    @input="onCategoryChange(file)"
                                />
                            </div>
                            <div class="upload-document-native__field">
                                <label class="upload-document-native__label">Subcategory</label>
                                <themed-select
                                    v-model="file.subcategoryId"
                                    :options="getSubcategoryOptions(file.categoryId)"
                                    :disabled="file.uploading || file.uploaded || !file.categoryId"
                                />
                            </div>
                        </div>

                        <!-- Remove Button (Icon only) -->
                        <div class="upload-document-native__file-actions">
                            <themed-button
                                variant="ghost"
                                size="icon"
                                class="upload-document-native__remove-btn"
                                @click="removeFile(index)"
                                :disabled="file.uploading"
                                title="Remove file"
                            >
                                <theme-icon icon="trash" type="icons" />
                            </themed-button>
                        </div>
                    </div>

                    <!-- Progress Bar -->
                    <div v-if="file.uploading" class="upload-document-native__file-progress">
                        <div 
                            class="upload-document-native__file-progress-bar" 
                            :style="{ width: file.progress + '%' }"
                        ></div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="file.errorMessage" class="upload-document-native__file-error-message">
                        {{ file.errorMessage }}
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <themed-button variant="secondary" @click="closeModal" :disabled="uploading">
                    Cancel
                </themed-button>
                <themed-button 
                    variant="default" 
                    @click="uploadDocuments" 
                    :disabled="uploading || !hasFiles || hasErrors"
                >
                    <theme-icon v-if="uploading" icon="loading" type="icons" class="animate-spin" />
                    <span v-else>{{ label }}</span>
                </themed-button>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
    name: 'UploadDocumentNative',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedInput,
        ThemedSelect,
        ThemeIcon
    },

    props: {
        documentableId: {
            type: Number,
            required: true
        },
        documentableType: {
            type: String,
            required: true
        },
        allowedCategories: {
            type: Array,
            required: false
        },
        maxFiles: {
            type: Number,
            default: 10
        },
        maxFileSize: {
            type: Number,
            default: 150 // MB
        },
        label: {
            type: String,
            default: "Upload Documents"
        }
    },

    data() {
        return {
            clientId: null,
            uploading: false,
            isDragging: false,
            files: [],
            errors: [],
            categories: [],
            fileIdCounter: 0
        };
    },

    computed: {
        ...mapState(['users', 'documents']),

        acceptedFiles() {
            return "image/*,.pdf,.mov,.avi,.mp3,.mp4,.mpeg,.wav,.rtf,.txt,.numbers,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odp,.ods,.odt,.oga,.ogv";
        },

        hasFiles() {
            return this.files.length > 0;
        },

        hasErrors() {
            return this.files.some(file => file.error);
        },

        filteredCategories() {
            if (!this.allowedCategories || !this.allowedCategories.length) {
                return this.categories;
            }
            return this.categories.filter(cat => 
                this.allowedCategories.includes(cat.name)
            );
        },

        categoryOptions() {
            return this.filteredCategories.map(cat => ({
                label: cat.name,
                value: cat.id
            }));
        }
    },

    mounted() {
        this.loadUserData();
        this.loadCategories();
    },

    methods: {
        async loadUserData() {
            await this.$store.dispatch('fetchCurrentUser');
            this.clientId = this.users.selectedUser.viewing_client_id;
        },

        async loadCategories() {
            try {
                const response = await axios.get('/api/documents/categories');
                this.categories = response.data;
            } catch (error) {
                console.error('Failed to load categories:', error);
            }
        },

        onModalOpened() {
            // Reset state when modal opens
            this.files = [];
            this.errors = [];
            this.uploading = false;
        },

        onModalClosed() {
            // Clean up
            this.files.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
            this.files = [];
            this.errors = [];
            this.uploading = false;
        },

        openFileDialog() {
            this.$refs.fileInput.click();
        },

        onFileSelect(event) {
            const selectedFiles = Array.from(event.target.files);
            this.addFiles(selectedFiles);
            // Reset input so same file can be selected again
            event.target.value = '';
        },

        onDragOver(event) {
            this.isDragging = true;
        },

        onDragLeave(event) {
            this.isDragging = false;
        },

        onDrop(event) {
            this.isDragging = false;
            const droppedFiles = Array.from(event.dataTransfer.files);
            this.addFiles(droppedFiles);
        },

        addFiles(newFiles) {
            this.errors = [];

            // Check max files limit
            if (this.files.length + newFiles.length > this.maxFiles) {
                this.errors.push({
                    fileName: 'Multiple files',
                    text: `Maximum ${this.maxFiles} files allowed`
                });
                return;
            }

            newFiles.forEach(file => {
                // Validate file size
                if (file.size > this.maxFileSize * 1000000) {
                    this.errors.push({
                        fileName: file.name,
                        text: 'File size is too large'
                    });
                    return;
                }

                // Create file object
                const fileObj = {
                    id: this.fileIdCounter++,
                    file: file,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    preview: null,
                    description: '',
                    categoryId: '',
                    subcategoryId: '',
                    uploading: false,
                    uploaded: false,
                    error: false,
                    errorMessage: '',
                    progress: 0
                };

                // Generate preview for images
                if (file.type.startsWith('image/')) {
                    fileObj.preview = URL.createObjectURL(file);
                }

                // Auto-detect category
                this.autoDetectCategory(fileObj);

                this.files.push(fileObj);
            });
        },

        removeFile(index) {
            const file = this.files[index];
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
            this.files.splice(index, 1);
        },

        autoDetectCategory(fileObj) {
            const fileName = fileObj.name;
            const fileType = fileObj.type;

            // Try to match category from filename
            for (const category of this.filteredCategories) {
                const words = fileName.split(/(?=[A-Z])|[\s_,.-]+/g);
                const categoryWords = category.name.toLowerCase().split(' ');
                
                if (words.some(word => categoryWords.includes(word.toLowerCase()))) {
                    fileObj.categoryId = category.id;
                    this.onCategoryChange(fileObj);
                    return;
                }
            }

            // Auto-detect for images
            if (!fileObj.categoryId && fileType.includes('image')) {
                const photoCategory = this.filteredCategories.find(cat => cat.name === 'Photo');
                if (photoCategory) {
                    fileObj.categoryId = photoCategory.id;
                    this.onCategoryChange(fileObj);
                }
            }

            // Auto-detect for videos
            if (!fileObj.categoryId && fileType.includes('video')) {
                const extrasCategory = this.filteredCategories.find(cat => cat.name === 'Extras');
                if (extrasCategory) {
                    fileObj.categoryId = extrasCategory.id;
                    this.onCategoryChange(fileObj);

                    // Set subcategory to Video
                    const videoSubcategory = this.documents.subcategories.find(
                        sub => sub.category_id === extrasCategory.id && sub.name === 'Video'
                    );
                    if (videoSubcategory) {
                        fileObj.subcategoryId = videoSubcategory.id;
                    }
                }
            }
        },

        onCategoryChange(file) {
            // Reset subcategory when category changes
            file.subcategoryId = '';
        },

        getSubcategories(categoryId) {
            if (!categoryId) return [];
            return this.documents.subcategories.filter(
                sub => sub.category_id === parseInt(categoryId)
            );
        },

        getSubcategoryOptions(categoryId) {
            const subcategories = this.getSubcategories(categoryId);
            return subcategories.map(sub => ({
                label: sub.name,
                value: sub.id
            }));
        },

        async uploadDocuments() {
            this.errors = [];
            this.uploading = true;

            const uploadPromises = this.files.map(file => this.uploadFile(file));

            try {
                await Promise.all(uploadPromises);

                // Check if all uploads were successful
                const hasErrors = this.files.some(f => f.error);
                
                if (!hasErrors) {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Documents Uploaded'
                    });
                    this.closeModal();
                }
            } catch (error) {
                console.error('Upload error:', error);
            } finally {
                this.uploading = false;
            }
        },

        async uploadFile(fileObj) {
            fileObj.uploading = true;
            fileObj.progress = 0;

            const formData = new FormData();
            formData.append('file', fileObj.file);
            formData.append('client_id', this.clientId);
            formData.append('documentable_id', this.documentableId);
            formData.append('documentable_type', this.documentableType);
            formData.append('category_id', fileObj.categoryId || '');
            formData.append('subcategory_id', fileObj.subcategoryId || '');
            formData.append('description', fileObj.description || '');

            try {
                const response = await axios.post('/documents', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        fileObj.progress = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                    }
                });

                fileObj.uploading = false;
                fileObj.uploaded = true;
                fileObj.progress = 100;

                // Emit success
                this.$emit('documents-uploaded', response.data.documents);
                this.$eventHub.$emit('add-to-gallery', response.data.documents);

            } catch (error) {
                fileObj.uploading = false;
                fileObj.error = true;
                fileObj.errorMessage = error.response?.data?.message || 'Upload failed';
                
                this.errors.push({
                    fileName: fileObj.name,
                    text: fileObj.errorMessage
                });
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        closeModal() {
            this.$modal.hide('document-upload');
        }
    }
};
</script>
